"use client"

import React, { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import { postRequest, getRequest } from '../../Fetch/request';
import Loader from '../Loader/Loader';
import DiagramDraw from '../Common/DiagramDraw';
import EvaluationFeedbackPanel from '../Common/EvaluationFeedbackPanel';
import { getAIFeedbackSync } from '../../Utils/aiUtils';
// Class diagram evaluation removed - COMBINED_DIAGRAM_EVALUATION_PROMPT no longer available
import { addLearningPointsToNotes } from '../../Utils/learningPointsUtils';
import { normalizeDiagramFeedback } from '../../Utils/diagramFeedbackUtils';
import { Box, Snackbar, Alert } from '@mui/material';
import './ClassDiagram.css';

export default function ClassDiagramDraw({ problemId: propProblemId, documentVisible = false, setDocumentVisible = () => {}, hideEvaluate = false, hideViewDoc = false, onSelectionChange = null, deselectAllCallback = null, onCopy = null, clearCopiedCallback = null, onUnsavedChangesChange = () => {} }) {
  const [classes, setClasses] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasLoadedFromLocalStorage, setHasLoadedFromLocalStorage] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationFeedback, setEvaluationFeedback] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isEvaluatingRef = useRef(false); // Guard to prevent multiple simultaneous calls

  const loc = useLocation();
  const problemId = propProblemId || new URLSearchParams(loc.search).get('problemId');

  // Use refs to store latest values for cleanup
  const classesRef = useRef(classes);
  const connectionsRef = useRef(connections);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

  // Keep refs in sync with state
  useEffect(() => {
    classesRef.current = classes;
    connectionsRef.current = connections;
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [classes, connections, hasUnsavedChanges]);

  // Notify parent component about unsaved changes status
  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // Save data before component unmounts (when switching steps)
  useEffect(() => {
    return () => {
      // Cleanup: Save data before unmounting using refs to get latest values
      if (problemId && problemId !== 'undefined' && problemId !== 'null') {
        const attemptKey = `lld-attempt-${problemId}`;
        try {
          const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          
          // Use refs to get the latest values (not stale closure values)
          const latestClasses = classesRef.current || [];
          const latestConnections = connectionsRef.current || [];
          const latestHasUnsavedChanges = hasUnsavedChangesRef.current || false;
          
          currentAttemptData.classDiagram = {
            items: latestClasses,
            connections: latestConnections,
            hasUnsavedChanges: latestHasUnsavedChanges,
          timestamp: Date.now()
        };
          currentAttemptData.timestamp = Date.now();
          localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
        } catch (error) {
          console.error('Failed to save to localStorage on unmount:', error);
        }
      }
    };
  }, [problemId]);

  // Load problem data and class diagram - check every time component mounts
  useEffect(() => {
      if (!problemId) {
        setLoading(false);
        return;
      }

    const fetchData = async () => {
      try {
        // First, always check localStorage first
        const attemptKey = `lld-attempt-${problemId}`;
        const attemptData = localStorage.getItem(attemptKey);
        
        if (attemptData) {
          try {
            const parsed = JSON.parse(attemptData);
            if (parsed.classDiagram && parsed.classDiagram.items && Array.isArray(parsed.classDiagram.items)) {
              const convertedClasses = parsed.classDiagram.items.map(cls => ({
                ...cls,
                content: typeof cls.content === 'string' 
                  ? cls.content 
                  : cls.content || '',
                headerColor: cls.headerColor || '#374151' // Ensure headerColor exists
              }));
              setClasses(convertedClasses);
              setConnections(parsed.classDiagram.connections || []);
              setHasUnsavedChanges(parsed.classDiagram.hasUnsavedChanges || false);
              setHasLoadedFromLocalStorage(true);
              
              // Data loaded from localStorage, no need to call backend
              setLoading(false);
              setHasInitialized(true);
              return;
            }
          } catch (localError) {
            console.error('Failed to parse attempt data:', localError);
          }
        }

        // Only fetch from backend if we don't have local data
        if (!hasLoadedFromLocalStorage) {
          try {
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            if (userId) {
              const diagramRes = await getRequest(
                `${process.env.REACT_APP_URL}/lld-attempt?userId=${userId}&lldProblemId=${problemId}`
              );
              
              if (diagramRes?.status === '200' && diagramRes.response?.classDiagram) {
                const { classBoxes, classDiagram } = diagramRes.response.classDiagram;
                
                // Convert classBoxes to our classes format
                const convertedClasses = (classBoxes || []).map(box => {
                  const attributes = (box.attributes || []).map(attr => {
                    if (typeof attr === 'string') return attr;
                    if (attr && typeof attr === 'object' && attr.text) return attr.text;
                    return '';
                  }).filter(attr => attr.trim());
                  
                  const methods = (box.methods || []).map(method => {
                    if (typeof method === 'string') return method;
                    if (method && typeof method === 'object' && method.text) return method.text;
                    return '';
                  }).filter(method => method.trim());
                  
                  let content = attributes.join('\n');
                  if (methods.length > 0) {
                    content += (content ? '\n\nMethods:\n' : 'Methods:\n') + methods.join('\n');
                  }
                  
                  return {
                    id: box.id,
                    name: box.name,
                    content: content,
                    position: box.position,
                    size: box.size || { width: 280, height: 280 },
                    headerColor: box.headerColor || '#374151', // Include headerColor from backend
                    selected: false
                  };
                });

                // Convert classDiagram objects to our connections format
                const convertedConnections = (classDiagram?.objects || [])
                  .filter(obj => obj.type === 'line' && obj.customData)
                  .map(obj => ({
                    id: obj.id || `conn_${Date.now()}_${Math.random()}`,
                    from: obj.customData.fromId,
                    to: obj.customData.toId,
                    type: obj.customData.type || 'association'
                  }));

                setClasses(convertedClasses);
                setConnections(convertedConnections);
              }
            }
          } catch (backendError) {
            console.error('Failed to load class diagram from backend:', backendError);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
        setHasInitialized(true);
      }
    };

    fetchData();
  }, [problemId, hasInitialized, hasLoadedFromLocalStorage]);

  // Convert our frontend format to backend format for saving
  const convertToBackendFormat = (classesData = classes, connectionsData = connections) => {
    const classBoxes = classesData.map(classObj => ({
      id: classObj.id,
      name: classObj.name,
      position: classObj.position,
      size: classObj.size,
      headerColor: classObj.headerColor || '#374151', // Include headerColor
      attributes: typeof classObj.content === 'string' 
        ? classObj.content.split('\n').filter(line => line.trim() && !line.toLowerCase().includes('methods:')).map((attr, index) => ({
        id: `attr_${classObj.id}_${index}`,
            text: attr.trim()
          }))
        : (classObj.attributes || []).map((attr, index) => ({
        id: `attr_${classObj.id}_${index}`,
            text: typeof attr === 'string' ? attr : attr.text || ''
          })),
      methods: typeof classObj.content === 'string'
        ? (() => {
            const methodsMatch = classObj.content.match(/methods?:/i);
            if (methodsMatch) {
              return classObj.content.split(/methods?:/i)[1].split('\n').filter(line => line.trim()).map((method, index) => ({
        id: `method_${classObj.id}_${index}`,
                text: method.trim()
              }));
            }
            return [];
          })()
        : (classObj.methods || []).map((method, index) => ({
        id: `method_${classObj.id}_${index}`,
            text: typeof method === 'string' ? method : method.text || ''
      }))
    }));

    const classDiagram = {
      version: '5.3.0',
      objects: connectionsData.map(conn => ({
        type: 'line',
        id: conn.id,
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        fill: 'transparent',
        stroke: '#000000',
        strokeWidth: 1,
        strokeDashArray: null,
        strokeLineCap: 'butt',
        strokeLineJoin: 'miter',
        strokeMiterLimit: 4,
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        flipX: false,
        flipY: false,
        opacity: 1,
        shadow: null,
        visible: true,
        backgroundColor: '',
        fillRule: 'nonzero',
        paintFirst: 'fill',
        globalCompositeOperation: 'source-over',
        transformMatrix: null,
        skewX: 0,
        skewY: 0,
        customData: {
          fromId: conn.from,
          toId: conn.to,
          type: conn.type
        }
      })),
      background: '#ffffff'
    };

    return { classBoxes, classDiagram };
  };

  // Handle evaluation of entire class diagram
  const handleEvaluate = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isEvaluatingRef.current) {
      return;
    }

    // Get current classes and connections at call time (not from closure)
    const currentClasses = classesRef.current || classes || [];
    const currentConnections = connectionsRef.current || connections || [];

    if (!problemId || currentClasses.length === 0) {
      setSnackbar({ open: true, message: 'Please add at least one class before evaluating.', severity: 'warning' });
      return;
    }

    isEvaluatingRef.current = true;
    setIsEvaluating(true);
    setEvaluationFeedback(null);

    // Class diagram evaluation has been removed
    alert('Class diagram evaluation is no longer available. Please use code evaluation instead.');
    setIsEvaluating(false);
    isEvaluatingRef.current = false;
  }, [problemId]); // Only depend on problemId (stable value)

  // Load evaluation feedback from sessionStorage on mount (session-only, clears on refresh)
  useEffect(() => {
    if (problemId) {
      const feedbackKey = `evaluation-feedback-lld-class-diagram-${problemId}`;
      const savedFeedback = sessionStorage.getItem(feedbackKey);
      if (savedFeedback) {
        try {
          const parsed = JSON.parse(savedFeedback);
          if (
            parsed?.feedback?.overall_assessment?.toLowerCase().includes('failed to evaluate')
          ) {
            sessionStorage.removeItem(feedbackKey);
          } else {
            setEvaluationFeedback(parsed.feedback);
          }
        } catch (e) {
          sessionStorage.removeItem(feedbackKey);
        }
      }
    }
  }, [problemId]);

  const handleSave = async () => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    
    // Read latest data from main attempt localStorage key
    const attemptKey = `lld-attempt-${problemId}`;
    const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    let dataToSave = { classes, connections };
    
    if (attemptData.classDiagram) {
      try {
        const classDiagramData = attemptData.classDiagram;
        if (classDiagramData.items && Array.isArray(classDiagramData.items)) {
          // Use data from localStorage
          setClasses(classDiagramData.items);
          setConnections(classDiagramData.connections || []);
          dataToSave = { classes: classDiagramData.items, connections: classDiagramData.connections || [] };
        }
      } catch (error) {
        console.error('Failed to parse localStorage data:', error);
        // Fallback to current state
      }
    }
    
    // Convert to backend format using the latest data from localStorage
    const { classBoxes, classDiagram } = convertToBackendFormat(dataToSave.classes, dataToSave.connections);
    
    setSaveStatus('saving');
    try {
      // IMPORTANT: Only send the field that was updated (classDiagram)
      await postRequest(
        process.env.REACT_APP_URL + '/lld-attempt/update-drawing',
        {
          userId: userId,
          lldProblemId: problemId,
          classDiagram: {
            classBoxes,
            classDiagram
          }
        }
      );
      setHasUnsavedChanges(false);
      // Update main attempt localStorage key to mark as saved
      // Preserve all existing data and only update classDiagram
      const updatedAttemptData = {
        ...attemptData, // Preserve all existing data
        classDiagram: {
          items: dataToSave.classes,
          connections: dataToSave.connections,
          hasUnsavedChanges: false,
          timestamp: Date.now()
        },
        timestamp: Date.now()
      };
      localStorage.setItem(attemptKey, JSON.stringify(updatedAttemptData));
      setSaveStatus('success');
      setSnackbar({ open: true, message: 'Class diagram saved successfully!', severity: 'success' });
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
      setSnackbar({ open: true, message: 'Failed to save class diagram. Please try again.', severity: 'error' });
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };


  if (loading) {
    return (
        <div style={{
          height: 'calc(100vh - 100px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fafafa'
        }}>
          <Loader />
        </div>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <DiagramDraw
          problemId={problemId}
          items={classes}
          setItems={setClasses}
          connections={connections}
          setConnections={setConnections}
          itemType="class"
          onSave={handleSave}
          saveStatus={saveStatus}
          hasUnsavedChanges={hasUnsavedChanges}
          setHasUnsavedChanges={setHasUnsavedChanges}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
          onEvaluate={hideEvaluate ? null : handleEvaluate}
          isEvaluating={isEvaluating}
          hideViewDoc={hideViewDoc}
          onSelectionChange={onSelectionChange}
          deselectAllCallback={deselectAllCallback}
          onCopy={onCopy}
          clearCopiedCallback={clearCopiedCallback}
          onSaveSnapshot={(snapshot) => {
            const attemptKey = `lld-attempt-${propProblemId}`;
            try {
              const current = JSON.parse(localStorage.getItem(attemptKey) || '{}');
              current.classSnapshot = snapshot;
              localStorage.setItem(attemptKey, JSON.stringify(current));
              window.dispatchEvent(new CustomEvent('lldDiagramSnapshotUpdated', { detail: { problemId: propProblemId } }));
            } catch (e) { /* quota exceeded */ }
          }}
        />
      </Box>
      {evaluationFeedback && (
        <EvaluationFeedbackPanel
          feedback={evaluationFeedback}
          problemId={propProblemId}
          problemType="LLD"
          onClose={() => {
            setEvaluationFeedback(null);
            // Clear from sessionStorage
            const feedbackKey = `evaluation-feedback-lld-class-diagram-${propProblemId}`;
            sessionStorage.removeItem(feedbackKey);
          }}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
